import { Visibility, VisibilityOff } from '@mui/icons-material'
import { Box, Button, FormControl, FormHelperText, Grid, IconButton, Input, InputAdornment, Typography, useTheme } from '@mui/material'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, useLocation, useParams } from 'react-router'
import logo from '../../../../../assets/Images/logos/logodark.svg'
import { resetPassword } from '../../../../../redux/actions/auth'
import { formValidator, resetFormValidator } from '../../../../../redux/actions/validator'
import { IRootState } from '../../../../../redux/types'
import CustomSnackBar from '../../../../shared/components/CustomSnackBar'
import ScreenLoading from '../../../../shared/components/ScreenLoading'
import assignPasswordStyles from '../styles/AssignPasswordStyles'

function useQuery() {
  return new URLSearchParams(useLocation().search)
}

function AssignPassword() {
  const { t } = useTranslation(['global'])
  const theme = useTheme()
  const classes = assignPasswordStyles(theme)

  let query = useQuery()
  let { token } = useParams<{ token: string }>()
  let emailQuery = query.get('email')

  const dispatch = useDispatch()
  const { isValidForm, errorForm, msgForm } = useSelector((state: IRootState) => state.validator)
  const { isAuth, loading, msg, error } = useSelector((state: IRootState) => state.auth)

  const [user, setUser] = useState({
    email: '',
    verificationCode: '',
    password: '',
    confirmPassword: '',
    showPassword: false,
    showForm: false,
    setPassword: true
  })

  React.useEffect(() => {
    if (token !== undefined && emailQuery != null) {
      setUser({ ...user, showForm: true, email: emailQuery, verificationCode: token })
    }
  }, [])

  React.useEffect(() => {
    if (isValidForm) {
      dispatch(resetFormValidator())
      dispatch(resetPassword({ user }))
    }
  }, [isValidForm])

  const handleClickShowPassword = () => {
    setUser({ ...user, showPassword: !user.showPassword })
  }

  const handleChange = (inputName: string) => (event: any) => {
    setUser({ ...user, [inputName]: event.target.value })
  }

  const handleSubmitPassword = () => {
    dispatch(
      formValidator({
        email: [user.email, 'required|max:45|min:3'],
        verificationCode: [user.verificationCode, 'required|max:10|min:3'],
        password: [user.password, 'required|max:45|min:3'],
        confirmPassword: [user.confirmPassword, `required|max:45|min:3|equal:${user.password}`]
      })
    )
  }

  if (isAuth) {
    return <Navigate to="/terms" replace />
  }

  return (
    <>
      <Grid container sx={classes.container}>
        <Grid sx={classes.form}>
          <Box mb={5} display="flex" flexDirection="column" alignItems="flex-start" gap={'32px'}>
            <img src={logo} width="86.37" alt="logo" />
          </Box>
          <Box mb={5}>
            <Typography color="textSecondary">{t('login.assing-password-text')}</Typography>
          </Box>

          <form noValidate autoComplete="off">
            <FormControl variant="filled" fullWidth>
              <Box mb={0.5}>
                <Typography>{t('login.new-password')}</Typography>
              </Box>
              <Input
                id="password1"
                required
                placeholder={t('login.new-password')}
                type={user.showPassword ? 'text' : 'password'}
                value={user.password}
                error={errorForm.password}
                onChange={handleChange('password')}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={event => event.preventDefault()}
                      edge="end"
                    >
                      {user.showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              <FormHelperText error id="outlined-helper-text">
                {msgForm.password}
              </FormHelperText>
            </FormControl>

            <FormControl variant="filled" fullWidth>
              <Box mb={0.5}>
                <Typography>{t('login.confirm-password')}</Typography>
              </Box>
              <Input
                id="password2"
                required
                placeholder={t('login.confirm-password')}
                type={user.showPassword ? 'text' : 'password'}
                value={user.confirmPassword}
                error={errorForm.confirmPassword}
                onChange={handleChange('confirmPassword')}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={event => event.preventDefault()}
                      edge="end"
                    >
                      {user.showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              <FormHelperText error id="outlined-helper-text">
                {msgForm.confirmPassword}
              </FormHelperText>
            </FormControl>

            <Box mt={4}>
              <Button variant="contained" fullWidth onClick={() => handleSubmitPassword()}>
                {t('login.login')}
              </Button>
            </Box>
          </form>
        </Grid>
      </Grid>
      <CustomSnackBar errors={[error]} msgs={[msg]} />
      <ScreenLoading loadings={[loading]} />
    </>
  )
}

export default AssignPassword
